import { useCallback, useMemo } from 'react';
import cn from 'clsx';
import { mapSearchStateToAiSearchContext } from '@pkgs/components/utils/tracking';
import { getPrivateOfficeAdvisorFinderUrl } from '@pkgs/components/utils/privateOfficeAdvisorFinderUrl';
import { useMapVisibilityStore } from '@pkgs/components/providers/MapVisibilityStoreProvider';
import { getConsentContext, useSearchModuleTracking } from '@pkgs/components/hooks/useSearchModuleTracking';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { PRIVATE_OFFICE_TILE_IMAGE_URL } from '@pkgs/components/consts/consts';
import { Headline } from '@ev-tech/ui-core-components/Headline';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { Button } from '@ev-tech/ui-core-components/Button';
import {
  type BusinessDivisionType,
  getAiSearchContext,
  getDivisionContext,
  getNavigationContext,
  trackNavigationEvent,
} from '@ev-tech/snowplow-library';

import styles from './PrivateOfficeCtaCard.module.css';

export const PRIVATE_OFFICE_CTA_ELEMENT_ID = 'tile_secondary_private-office';

type PrivateOfficeCtaCardProps = {
  className?: string;
};

export const PrivateOfficeCtaCard = ({ className }: PrivateOfficeCtaCardProps) => {
  const {
    t,
    ImageComponent,
    options: { language },
  } = useAppContext();
  const { pageContext, shopContext, mapABTestContext } = useSearchModuleTracking();
  const isMapToggledOn = useMapVisibilityStore((state) => state.isMapActive);
  const objectTypeCategory = useSearchStore((state) => state.objectTypeCategory);
  const preferences = useSearchStore((state) => state.preferences);
  const searchMode = useSearchStore((state) => state.searchMode);
  const placeIds = useSearchStore((state) => state.placeIds);

  const href = useMemo(() => getPrivateOfficeAdvisorFinderUrl({ language, placeId: placeIds?.[0] }), [language, placeIds]);

  const handleClick = useCallback(() => {
    trackNavigationEvent({
      screenName: '',
      contexts: [
        getNavigationContext({
          element_id: PRIVATE_OFFICE_CTA_ELEMENT_ID,
          target_url: href,
        }),
        pageContext,
        shopContext,
        getDivisionContext({
          name: objectTypeCategory as BusinessDivisionType,
        }),
        getAiSearchContext(
          mapSearchStateToAiSearchContext({
            preferences,
            isAiSearchEnabled: searchMode === SearchMode.AI,
          }),
        ),
        getConsentContext(),
        mapABTestContext(isMapToggledOn),
      ],
    });
  }, [href, pageContext, objectTypeCategory, shopContext, preferences, searchMode, mapABTestContext, isMapToggledOn]);

  return (
    <article className={cn(styles.article, className)} data-testid="search-components_cta-card_private-office">
      <ImageComponent
        src={PRIVATE_OFFICE_TILE_IMAGE_URL}
        alt={t('search.privateOffice.ctaCard.imageAlt')}
        width={133}
        height={86}
        className={styles.image}
      />
      <div className={styles.textContainer}>
        <Headline className={styles.title} label={t('search.privateOffice.ctaCard.title')} size="h5" />
        <Copy text={t('search.privateOffice.ctaCard.text')} />
      </div>

      <Button
        as="a"
        href={href}
        className={styles.button}
        size="medium"
        variant="primary"
        label={t('search.privateOffice.ctaCard.button')}
        testId="search-components_cta-card_private-office_button"
        onClick={handleClick}
      />
    </article>
  );
};
